import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, BadgeCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { REVIEWS } from "@/staticData";

const AGE_LABEL = { ro: "ani", en: "yrs", it: "anni", de: "Jahre", he: "שנים", pl: "lata" };

export default function Reviews({ config }) {
  const { t, lang } = useLang();
  const [reviews] = useState(() => [...REVIEWS].sort((a, b) => b.review_date.localeCompare(a.review_date)));

  const scores = [
    [t("rev.cleanliness"), config?.score_cleanliness || "9,9"],
    [t("rev.comfort"), config?.score_comfort || "9,8"],
    [t("rev.host"), config?.score_host || "9,7"],
    [t("rev.location"), config?.score_location || "9,6"],
    [t("rev.value"), config?.score_value || "9,6"],
  ];

  return (
    <section id="recenzii" className="py-24 md:py-32" data-testid="reviews-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <motion.p initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
                className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
                <span className="inline-block w-10 h-px bg-terracotta" />
                {t("rev.eyebrow")}
              </motion.p>
              <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
                className="mt-5 font-serif font-medium text-4xl md:text-5xl leading-[1.05] tracking-tight text-main">
                {t("rev.title")}
              </motion.h2>

              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-10 bg-cream-light border border-warm-grey/40 rounded-3xl p-8" data-testid="reviews-score-card">
                <div className="flex items-end gap-3">
                  <span className="font-serif text-7xl leading-none text-main">{config?.score_general || "9,6"}</span>
                  <span className="text-brown/60 pb-2">/ 10</span>
                </div>
                <p className="mt-1 text-sm text-brown/70">{t("rev.general")} · {config?.reviews_count || "62"} {t("rev.count")}</p>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="reviews-sources">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-sage/20 text-green-dark text-xs font-bold px-3.5 py-1.5" data-testid="reviews-booking-score">
                    <Star size={12} className="fill-green-dark" /> {t("rev.booking")}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-sage/20 text-green-dark text-xs font-bold px-3.5 py-1.5" data-testid="reviews-google-score">
                    <Star size={12} className="fill-green-dark" /> {t("rev.google")}
                  </span>
                </div>
                <div className="mt-6 space-y-3">
                  {scores.map(([label, val]) => (
                    <div key={label}>
                      <div className="flex justify-between text-xs font-semibold text-brown mb-1">
                        <span>{label}</span><span>{val}</span>
                      </div>
                      <div className="h-1.5 bg-warm-grey/50 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${parseFloat(String(val).replace(",", ".")) * 10}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.2, ease: "easeOut" }}
                          className="h-full bg-gradient-to-r from-sage to-green-dark rounded-full"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-xs text-brown/50 flex items-center gap-1.5">
                  <BadgeCheck size={14} className="text-green-dark" /> {t("rev.verified")}
                </p>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: 0.15 }}
                className="mt-6 overflow-hidden rounded-3xl border border-warm-grey/40 shadow-lg shadow-main/5 group">
                <img src="/assets/reviews-fire-song.webp" alt="Oaspeți cântând la focul de seară în grădina Imperfect House" loading="lazy"
                  className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700" data-testid="reviews-image" />
              </motion.div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="columns-1 sm:columns-2 gap-5" data-testid="reviews-grid">
              {reviews.map((r, i) => (
                <motion.article
                  key={r.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: Math.min(i * 0.06, 0.3) }}
                  className="break-inside-avoid mb-5 bg-cream-light border border-warm-grey/40 rounded-3xl p-7 flex flex-col hover:border-terracotta/50 hover:shadow-lg hover:shadow-terracotta/5 transition-all duration-300"
                  data-testid={`review-${r.id}`}
                >
                  <Quote size={26} className="text-terracotta/50" />
                  <p className="mt-4 text-sm md:text-base text-brown font-light leading-relaxed flex-1">„{r.text}"</p>
                  <div className="mt-6 pt-5 border-t border-warm-grey/40 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-main text-sm">{r.guest_name}{r.age ? ` · ${r.age} ${AGE_LABEL[lang] || "ani"}` : ""}</p>
                      <p className="text-xs text-brown/60 mt-0.5">{r.source}</p>
                    </div>
                    <span className="flex items-center gap-1 bg-green-dark text-cream-light text-sm font-bold rounded-full px-3 py-1.5">
                      <Star size={13} className="fill-cream-light" /> {String(r.score).replace(".", ",")}
                    </span>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
